import {
  EOrganization,
  EUser,
  ERequestTypes,
  EResponseTypes,
  ESnapshotExists
} from 'lib/types';
import {
  ImplementationStatus,
  OrganizationStatus,
  OrganizationType,
  Product,
  State
} from 'lib/enums';
import api from 'api';
import { logAndCaptureException } from 'utils';
import {
  SearchableOrganizationRecord,
  SearchableOrganizationRecordFilter
} from 'lib/types/searchable';
import { getLocationParams } from 'lib/frontend/utils/browser';
import { ColumnService } from 'lib/services/directory';
import { AdjudicationAreaWithId } from 'lib/types/adjudicationArea';

/* Finds which papers the user can place in. For this we have the following cases:
 * 1- If the user is a publisher, they are restricted to place in the papers they have access to only.
 * 2- If the placement URL has `restrictedPapers` param, these are the papers, the user can only place in.
 * 3- Otherwise, use can place in any valid paper
 * */
export const getRestrictedPublisherIds = (
  user: ESnapshotExists<EUser> | null,
  isPublisher: boolean,
  availableOrganizations: ESnapshotExists<EOrganization>[]
) => {
  let publisherIds: string[] = [];

  // Publishers are allowed only to place in their papers
  if (isPublisher) {
    publisherIds = availableOrganizations.map(org => org.id);
  }

  // We can restrict placement in specific papers by having paper ids in the URL
  const locationRestrictedPublisherIds =
    getRestrictedPublishersFromLocationParams();

  if (locationRestrictedPublisherIds) {
    publisherIds = locationRestrictedPublisherIds;
  }

  return publisherIds;
};

export type AdjudicationFilters = {
  stateFilter?: AdjudicationAreaWithId;
  countyFilter?: AdjudicationAreaWithId;
  cityFilter?: AdjudicationAreaWithId;
};

export type PublisherFilters = {
  locationFilter: {
    stateFilter?: number;
  };
  adjudicationFilter?: AdjudicationFilters;
};

// Fetches all available papers and states to be used in the confirm newspaper step
export const searchPublisherOrganizations = async ({
  isUserPublisher,
  stateFilter,
  search = '',
  restrictedPublisherIds,
  product = Product.Notice,
  adjudicationFilters,
  fetchAllOrganizations = true,
  pageSize,
  showOnlyColumnCertified = false
}: {
  isUserPublisher: boolean;
  stateFilter?: number;
  search?: string;
  restrictedPublisherIds?: string[];
  product?: Product;
  adjudicationFilters?: AdjudicationFilters;
  fetchAllOrganizations?: boolean;
  pageSize?: number;
  showOnlyColumnCertified?: boolean;
}): Promise<{
  publisherOrganizations: SearchableOrganizationRecord[];
  states: number[];
}> => {
  // Construct the Elastic filters
  const filters: SearchableOrganizationRecordFilter[] = [];
  const noneFilters: SearchableOrganizationRecordFilter[] = [];

  // optional filters for adjudication area
  if (adjudicationFilters) {
    const {
      stateFilter: adjudicationState,
      countyFilter,
      cityFilter
    } = adjudicationFilters;
    if (adjudicationState) {
      filters.push({
        states: [adjudicationState.fipsCode]
      });
    }

    if (countyFilter) {
      filters.push({
        counties: [`${adjudicationState?.fipsCode}${countyFilter.fipsCode}`]
      });
    }

    if (cityFilter) {
      filters.push({
        cities: [`${adjudicationState?.fipsCode}${cityFilter.fipsCode}`]
      });
    }
  }
  if (stateFilter) {
    filters.push({
      state: [stateFilter]
    });
  }

  // ONCALL-3647 Temporarily filtering on frontend to fix bug not returning correct results with ID filter
  if (restrictedPublisherIds && restrictedPublisherIds.length) {
    filters.push({ organizationid: restrictedPublisherIds });
  }

  // Only publishers can place in a disabled paper
  if (!isUserPublisher) {
    // We'll eventually have separate disabled statuses for each product line
    if (product === Product.Notice) {
      filters.push({ disabled: [0] });
    }
  }

  filters.push({ type: [OrganizationType.newspaper.value] });

  if (product === Product.Notice) {
    filters.push({ isvalidpaper: [1] });
    noneFilters.push({ status: [OrganizationStatus.disengaged.value] });
  } else if (product === Product.Obituary) {
    filters.push({ isacceptingobituaries: [1] });
    filters.push({ obituaryimplementationstatus: [ImplementationStatus.Live] });
  } else if (product === Product.Classified) {
    filters.push({ isacceptingclassifieds: [1] });
    filters.push({
      classifiedimplementationstatus: [ImplementationStatus.Live]
    });
  }

  if (showOnlyColumnCertified) {
    filters.push({
      status: [
        OrganizationStatus.live.value,
        OrganizationStatus.in_implementation.value
      ]
    });
  }

  const postBody: ERequestTypes['search/organizations'] = {
    search,
    filters,
    noneFilters,
    fetchAllOrganizations,
    ...(pageSize ? { pageSize } : {})
  };

  try {
    const { results, error }: EResponseTypes['search/organizations'] =
      await api.post('search/organizations', postBody);

    const filteredResults =
      restrictedPublisherIds && restrictedPublisherIds.length > 0
        ? results.filter(result => {
            return restrictedPublisherIds.includes(result.id);
          })
        : results;

    if (error) {
      logAndCaptureException(
        ColumnService.WEB_PLACEMENT,
        new Error('Unable to search organizations'),
        `Placement: Error in searchPublisherOrganizations. Filters: ${JSON.stringify(
          filters
        )}`
      );
    }

    const states = new Set(results.map(paper => paper.state));
    return { states: [...states], publisherOrganizations: filteredResults };
  } catch (e) {
    logAndCaptureException(
      ColumnService.WEB_PLACEMENT,
      e,
      'Placement: Error in searchPublisherOrganizations'
    );
    return { publisherOrganizations: [], states: [] };
  }
};

export function getStateFromLocationParams() {
  const state = getLocationParams().get('state');
  if (!state) {
    return;
  }

  const existingState = State.by_label(state);
  if (!existingState) {
    return;
  }

  const selectedState = existingState.value;
  if (!selectedState) {
    return;
  }

  return selectedState;
}

export function getRestrictedPublishersFromLocationParams() {
  const restrictedPapers = getLocationParams().get('restrictedPapers');
  if (!restrictedPapers) {
    return null;
  }

  return restrictedPapers.split(',');
}

export const removeSubdomainFromHostIfExists = (host: string) => {
  const prefix = host.split('.')[0];
  if (!['column', 'demo', 'staging', 'localhost:3000'].includes(prefix)) {
    return host.split(`${prefix}.`)[1];
  }
  return ''; // no subdomain exists
};
